import { Modal, Select, Tabs, message } from 'antd';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CourseOption from '../../components/CourseOption';
import Course from '../../course/types/Course';
import { useUser } from '../../user/userContext';
import OpenClassAPI from '../OpenClassAPI';
import { useCourseCertificateTemplates } from '../hooks/useCourseCertificateTemplates';
import { CreateOpenClassParams } from '../types/OpenClass';
import SessionFields from './SessionFields';
import {
  SessionDraft,
  emptySessionDraft,
  isSessionDraftValid,
  resizeSessionDrafts,
  sessionDraftToCreateParams,
} from './sessionDraft';
import {
  findSessionOverlapPairs,
  formatSessionDateTimeRange,
} from './sessionTiming';
import { certificateTemplateOptions } from '../utils/openClass';

type OpenClassFormModalProps = {
  open: boolean;
  courses: Course[];
  onClose: () => void;
  onCreated: () => void;
};

const maxSessions = 10;

const OpenClassFormModal: React.FC<OpenClassFormModalProps> = ({
  open,
  courses,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const [modal, modalContextHolder] = Modal.useModal();
  const [courseId, setCourseId] = useState<number>();
  const [selectedCertificateTemplateIds, setSelectedCertificateTemplateIds] =
    useState<number[]>([]);
  const {
    templates: certificateTemplates,
    loading: loadingCertificateTemplates,
  } = useCourseCertificateTemplates(courseId);
  const [sessions, setSessions] = useState<SessionDraft[]>([
    emptySessionDraft(),
  ]);
  const [activeTab, setActiveTab] = useState('0');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCourseId(undefined);
      setSelectedCertificateTemplateIds([]);
      setSessions([emptySessionDraft()]);
      setActiveTab('0');
    }
  }, [open]);

  const changeSessionCount = useCallback((count: number) => {
    setSessions((current) => resizeSessionDrafts(current, count));
    setActiveTab((current) =>
      Number(current) >= count ? String(count - 1) : current,
    );
  }, []);

  const updateSession = useCallback((index: number, next: SessionDraft) => {
    setSessions((current) =>
      current.map((session, i) => (i === index ? next : session)),
    );
  }, []);

  const createOpenClass = useCallback(
    async (params: CreateOpenClassParams) => {
      setSubmitting(true);
      try {
        await OpenClassAPI.createOpenClass(params);
        onCreated();
        onClose();
      } catch (error) {
        // The backend's refusal reason is already translated and shown by the interceptor.
        if (isAxiosError(error) && !error.response?.data.message) {
          message.error(t('views.OpenClasses.saveFailed'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onClose, onCreated, t],
  );

  const submit = useCallback(() => {
    if (courseId == null || selectedCertificateTemplateIds.length === 0) {
      message.error(t('views.OpenClasses.saveFailed'));
      return;
    }

    const invalidIndex = sessions.findIndex(
      (session) => !isSessionDraftValid(session),
    );
    if (invalidIndex >= 0) {
      setActiveTab(String(invalidIndex));
      message.error(t('views.OpenClasses.invalidSession'));
      return;
    }

    const params: CreateOpenClassParams = {
      courseId,
      certificateTemplateIds: selectedCertificateTemplateIds,
      sessions: sessions.map(sessionDraftToCreateParams),
    };

    const overlapPairs = findSessionOverlapPairs(sessions);
    if (overlapPairs.length > 0) {
      modal.confirm({
        cancelText: t('common.cancel'),
        content: (
          <div>
            <div className="space-y-2">
              {overlapPairs.map(([firstIndex, secondIndex]) => (
                <div key={`${firstIndex}-${secondIndex}`}>
                  {t('views.OpenClasses.overlapPair', {
                    firstNumber: firstIndex + 1,
                    firstRange: formatSessionDateTimeRange(
                      sessions[firstIndex],
                    ),
                    secondNumber: secondIndex + 1,
                    secondRange: formatSessionDateTimeRange(
                      sessions[secondIndex],
                    ),
                  })}
                </div>
              ))}
            </div>
            <p className="mb-0 mt-4">
              {t('views.OpenClasses.overlapConfirmation')}
            </p>
          </div>
        ),
        okText: t('common.create'),
        okType: 'primary',
        title: t('views.OpenClasses.overlapTitle'),
        onOk: () => createOpenClass(params),
      });
      return;
    }

    void createOpenClass(params);
  }, [
    courseId,
    createOpenClass,
    modal,
    selectedCertificateTemplateIds,
    sessions,
    t,
  ]);

  return (
    <>
      <Modal
        destroyOnClose
        confirmLoading={submitting}
        okText={t('common.create')}
        open={open}
        title={t('views.OpenClasses.createOpenClass')}
        width={1200}
        onCancel={onClose}
        onOk={submit}>
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="flex min-w-[16rem] flex-1 items-center gap-2">
            <span className="whitespace-nowrap">
              <span className="text-red-700">*</span> {t('common.course')}
            </span>
            <Select
              className="w-full"
              filterOption={(input, option) =>
                option?.props.children.props.course.name
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              placeholder={t('views.OpenClasses.selectCourse')}
              size="large"
              showSearch
              value={courseId}
              onChange={(value) => {
                setCourseId(value);
                setSelectedCertificateTemplateIds([]);
              }}>
              {courses.map((course) => (
                <Select.Option key={course.id} value={course.id}>
                  <CourseOption course={course} user={currentUser} />
                </Select.Option>
              ))}
            </Select>
          </label>
          <label className="flex min-w-[16rem] flex-1 items-center gap-2">
            <span className="whitespace-nowrap">
              <span className="text-red-700">*</span>{' '}
              {t('views.OpenClasses.certificateTypes')}
            </span>
            <Select
              className="w-full"
              mode="multiple"
              placeholder={t('views.OpenClasses.selectCertificates')}
              size="large"
              disabled={courseId == null || loadingCertificateTemplates}
              loading={loadingCertificateTemplates}
              value={selectedCertificateTemplateIds}
              options={certificateTemplateOptions(certificateTemplates)}
              onChange={setSelectedCertificateTemplateIds}
            />
          </label>
        </div>
        <Tabs
          activeKey={activeTab}
          destroyInactiveTabPane
          tabBarExtraContent={{
            left: (
              <label className="mr-6 flex items-center gap-2">
                <span className="whitespace-nowrap">
                  {t('views.OpenClasses.numberOfSessions')}
                </span>
                <Select
                  className="w-20"
                  value={sessions.length}
                  options={Array.from({ length: maxSessions }, (_, i) => ({
                    label: i + 1,
                    value: i + 1,
                  }))}
                  size="large"
                  onChange={changeSessionCount}
                />
              </label>
            ),
          }}
          items={sessions.map((session, index) => ({
            key: String(index),
            label: t('views.OpenClasses.sessionNumber', { number: index + 1 }),
            children: (
              <SessionFields
                showInstructorSearch
                courseId={courseId}
                value={session}
                onChange={(next) => updateSession(index, next)}
              />
            ),
          }))}
          onChange={setActiveTab}
        />
      </Modal>
      {modalContextHolder}
    </>
  );
};

export default OpenClassFormModal;
